import styled from "@emotion/styled";
import { FC, ReactNode } from "react";

import { Check, ChevronRight } from "@livechat/design-system-icons";
import {
  DesignToken,
  Heading,
  Icon,
  RadiusToken,
  SpacingToken,
  Text,
  TransitionDurationToken,
} from "@livechat/design-system-react-components";

import { withVar } from "themes/helpers";

export interface IStepItemProps {
  completed: boolean;
  title: ReactNode;
  order: number;
  href?: string;
  target?: "_blank";
  onClick?: VoidFunction;
}

export const StepItem: FC<IStepItemProps> = ({ completed, title, order, href, target, onClick }) => (
  <Wrapper
    data-testid="step-item"
    onClick={completed ? undefined : onClick}
    completed={completed}
    {...(completed ? {} : { role: "button" })}
    {...(href ? { as: "a", target, href } : {})}
  >
    <CompletedBox completed={completed}>
      {completed ? (
        <Icon data-testid="step-item-complete-icon" source={Check} kind="success" size="small" />
      ) : (
        <Text bold>{order}</Text>
      )}
    </CompletedBox>
    <Title completed={completed} size="xs">
      {title}
    </Title>
    {!completed && <ChevronRightIcon source={ChevronRight} />}
  </Wrapper>
);

const CompletedBox = styled("div", {
  shouldForwardProp: prop => prop !== "completed",
})<{ completed: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${(p): string =>
    withVar(p.completed ? DesignToken.DecorGreen100 : DesignToken.SurfaceSecondaryDefault)};
`;

const ChevronRightIcon = styled(Icon)`
  transition: transform ${withVar(TransitionDurationToken.Moderate1)} ease-in-out;
`;

const Wrapper = styled("div", {
  shouldForwardProp: prop => prop !== "completed",
})<{ completed: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing4)};

  padding: ${withVar(SpacingToken.Spacing4)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  border: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  cursor: ${(p): string => (p.completed ? "default" : "pointer")};
  transition: all ${withVar(TransitionDurationToken.Moderate1)};
  color: ${withVar(DesignToken.ContentBasicPrimary)};
  text-decoration: none;

  &:hover {
    ${ChevronRightIcon} {
      transform: translateX(4px);
    }
  }
`;

const Title = styled(Heading, {
  shouldForwardProp: prop => prop !== "completed",
})<{ completed: boolean }>`
  margin-right: auto;
  color: ${(p): string => withVar(p.completed ? DesignToken.ContentBasicDisabled : DesignToken.ContentBasicPrimary)};

  mark {
    background-color: transparent;
    color: ${(p): string => withVar(p.completed ? DesignToken.ContentBasicDisabled : DesignToken.ActionPrimaryDefault)};
  }
`;
